import React, { useEffect, useState, useCallback } from 'react';
import StyledHeadingText from '../components/Fonts/StyledHeadingText';
import CustomLoading from '../components/customLoading/CustomLoading';
import Breadcrumbs from '../components/BreadCrumbs';
import Pagination from '../components/tables/pagination/Pagination';
import SearchBar from '../components/searchBar/SearchBar';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/FormatDateTime';
import { BiRefresh } from 'react-icons/bi';
import { Zap, ZapOff, Activity, Cpu, Users, CheckCircle2, XCircle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import ShowModal from '../utils/ShowModal';
import IotDeviceDetailsPop from './IotDeviceDetailsPop';
import { getIotDevices, getIotDeviceStats } from '../services/iotDevices';

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, accent }) {
    return (
        <div
            style={{
                background: 'var(--card-bg, #ffffff)',
                border: `1px solid ${accent}33`,
                borderLeft: `4px solid ${accent}`,
                borderRadius: '10px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                boxShadow: `0 2px 12px ${accent}15`,
                transition: 'transform 0.15s, box-shadow 0.15s',
                cursor: 'default',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${accent}25`;
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 2px 12px ${accent}15`;
            }}
        >
            <div style={{ background: `${accent}15`, borderRadius: '8px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={22} color={accent} />
            </div>
            <div>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#888', lineHeight: 1 }}>{title}</p>
                <p style={{ margin: '4px 0 0', fontSize: '26px', fontWeight: 700, color: accent, lineHeight: 1 }}>{value ?? '—'}</p>
            </div>
        </div>
    );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function Badge({ active, label }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 600,
            background: active ? '#dcfce7' : '#fee2e2',
            color: active ? '#16a34a' : '#dc2626',
        }}>
            {active ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
            {label}
        </span>
    );
}

// ─── Sortable TH ──────────────────────────────────────────────────────────────
function SortableTh({ label, field, sortBy, sortOrder, onSort, style = {} }) {
    const isActive = sortBy === field;
    return (
        <th
            onClick={() => onSort(field, isActive ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc')}
            style={{
                ...th,
                cursor: 'pointer',
                userSelect: 'none',
                color: isActive ? '#3b6cf8' : '#888',
                background: isActive ? '#f0f4ff' : '#f8f9fc',
                ...style,
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {label}
                {isActive
                    ? sortOrder === 'asc'
                        ? <ChevronUp size={12} />
                        : <ChevronDown size={12} />
                    : <ChevronsUpDown size={12} color="#ccc" />
                }
            </span>
        </th>
    );
}

// ─── Device Row ───────────────────────────────────────────────────────────────
function DeviceRow({ item, index, onClick }) {
    const subExpired = item.subscription_end_date && new Date(item.subscription_end_date) < new Date();
    return (
        <tr
            onClick={() => onClick(item)}
            style={{
                background: subExpired ? '#fff5f5' : '#fff',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = subExpired ? '#ffe4e4' : '#f7f9ff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = subExpired ? '#fff5f5' : '#fff'; }}
        >
            <td style={td}>{index + 1}</td>
            <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600, color: '#3b6cf8', fontSize: '13px' }}>{item.device_id}</td>
            <td style={td}>{item.device_name || '—'}</td>
            <td style={{ ...td, textAlign: 'center' }}>
                {item.is_motor_on
                    ? <span style={{ color: '#f59e0b', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Zap size={14} /> Running</span>
                    : <span style={{ color: '#999', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ZapOff size={14} /> Off</span>}
            </td>
            <td style={{ ...td, textAlign: 'center' }}>
                <Badge active={item.electricity === '1'} label={item.electricity === '1' ? 'Present' : 'Cut'} />
            </td>
            <td style={{ ...td, fontSize: '12px', color: '#555' }}>{item.created_at ? formatDate(item.created_at) : '—'}</td>
            <td style={{ ...td, fontSize: '12px' }}>
                <span style={{ color: subExpired ? '#dc2626' : '#555', fontWeight: subExpired ? 700 : 400 }}>
                    {item.subscription_end_date ? formatDate(item.subscription_end_date) : '—'}
                </span>
            </td>
        </tr>
    );
}

const th = {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#888',
    background: '#f8f9fc',
    borderBottom: '2px solid #eef0f6',
    whiteSpace: 'nowrap',
};
const td = {
    padding: '11px 14px',
    fontSize: '13px',
    color: '#333',
    verticalAlign: 'middle',
};

// ─── Main Component ───────────────────────────────────────────────────────────
function IotDevices() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [stats, setStats] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const pageSize = 10;

    const fetchDevices = useCallback(async (query = '', sb = null, so = 'desc') => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (query) params.set('search', query);
            if (sb) {
                params.set('sort_by', sb);
                params.set('sort_order', so);
            }
            const qs = params.toString() ? `?${params.toString()}` : '';
            const res = await getIotDevices(qs);
            if (res.status === 200) {
                setData(res.data);
            } else {
                toast.error('Unable to fetch IoT devices');
            }
        } catch (err) {
            console.error('Error fetching IoT devices:', err);
            toast.error('Error fetching IoT devices');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const res = await getIotDeviceStats();
            if (res.status === 200) setStats(res.data);
        } catch (err) {
            console.error('Error fetching IoT stats:', err);
        }
    }, []);

    useEffect(() => {
        fetchDevices('');
        fetchStats();
    }, [fetchDevices, fetchStats]);

    const handleSearch = (query) => {
        setSearchText(query);
        setCurrentPage(1);
        fetchDevices(query, sortBy, sortOrder);
    };

    const handleSortChange = (field, order) => {
        setSortBy(field);
        setSortOrder(order);
        setCurrentPage(1);
        fetchDevices(searchText, field, order);
    };

    const resetFilters = () => {
        setSearchText('');
        setSortBy(null);
        setSortOrder('desc');
        setCurrentPage(1);
        fetchDevices('');
    };

    const openModal = (item) => { setOpeningItem(item); setIsOpen(true); };
    const closeModal = () => { setIsOpen(false); setOpeningItem({}); };
    const handlePageChange = (page) => setCurrentPage(page);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Devices', link: '/devices' },
                { name: 'IoT Devices' },
            ]} />
            <div className="py-2 flex flex-row gap-2 items-center">
                <StyledHeadingText text="IoT Devices" />
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', margin: '8px 0 20px' }}>
                <StatCard title="Total Devices" value={stats?.total_devices} icon={Cpu} accent="#3b6cf8" />
                <StatCard title="Total Users" value={stats?.total_users} icon={Users} accent="#8b5cf6" />
                <StatCard title="Active" value={stats?.active_devices} icon={Activity} accent="#16a34a" />
                <StatCard title="Inactive" value={stats?.inactive_devices} icon={XCircle} accent="#ef4444" />
            </div>

            {/* Filters */}
            <div className="py-2 flex flex-row gap-2 items-center flex-wrap">
                <SearchBar
                    placeholder="Search by Device ID or Device Name or User Mobile Number"
                    onSearch={handleSearch}
                    value={searchText}
                />
                {(searchText.length > 0 || sortBy) && (
                    <BiRefresh
                        size={32}
                        className="cursor-pointer text-primary"
                        onClick={resetFilters}
                        title="Reset all filters"
                    />
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex w-full flex-row justify-center my-2">
                    <CustomLoading size={100} />
                </div>
            ) : (
                <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef0f6', boxShadow: '0 2px 12px #0000000a' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>#</th>
                                <th style={th}>Device ID</th>
                                <th style={th}>Device Name</th>
                                <SortableTh label="Motor"       field="is_motor_on"           sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} style={{ textAlign: 'center' }} />
                                <SortableTh label="Electricity" field="electricity"            sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} style={{ textAlign: 'center' }} />
                                <SortableTh label="Created At"  field="created_at"            sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} />
                                <SortableTh label="Sub. End"    field="subscription_end_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSortChange} />
                            </tr>
                        </thead>
                        <tbody>
                            {data?.results?.length > 0
                                ? data.results.map((item, idx) => (
                                    <DeviceRow key={item.device_id} item={item} index={idx} onClick={openModal} />
                                ))
                                : (
                                    <tr>
                                        <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa', padding: '40px' }}>
                                            No IoT devices found.
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {data.count > 0 && (
                <Pagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalEntries={data?.count}
                    onPageChange={handlePageChange}
                    data={data}
                    setData={setData}
                />
            )}

            {/* Detail Modal */}
            <ShowModal
                contentLabel="IoT Device Details"
                isOpen={modalIsOpen}
                modalWidth="60%"
                isSidebar={true}
                onRequestClose={closeModal}
                componentToPass={<IotDeviceDetailsPop item={openingItem} />}
            />
        </>
    );
}

export default IotDevices;